import type { IParsedProject } from "@frontend-shared/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { useCreateProject } from "@/api/project/useCreateProject";
import { useEditProject } from "@/api/project/useEditProject";
import { FormSubmitButton } from "@/components/FormSubmitButton";
import { StyledInput } from "@/components/StyledInput";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useAuth } from "@/hooks/use-auth";
import { FORM_VALIDATION_OPTIONS } from "@/lib/validations/form-defaults";

interface ICreateUpdateProjectDialogContentProps {
  organizationUuid: string | undefined;
  organizationName?: string;
  onOpenChange: (open: boolean) => void;
  project?: IParsedProject | null;
  isUpdateCurrentProject?: boolean;
}

const CreateUpdateProjectDialogContent = ({
  organizationUuid,
  organizationName,
  onOpenChange,
  project,
  isUpdateCurrentProject,
}: ICreateUpdateProjectDialogContentProps) => {
  const { t } = useTranslation(["settings", "common"]);

  const { onCurrentProjectChange } = useAuth();

  const schema = z.object({
    title: z
      .string()
      .min(1, t("projects.form.nameRequired"))
      .min(3, t("projects.form.nameMin"))
      .max(100, t("projects.form.nameMax")),
  });

  const form = useForm<z.infer<typeof schema>>({
    ...FORM_VALIDATION_OPTIONS,
    resolver: zodResolver(schema),
    defaultValues: { title: project?.title || "" },
  });

  const { mutate: createProject, isPending: isCreating } = useCreateProject();

  const { mutate: editProject, isPending: isEditing } = useEditProject();

  const onSubmit = async (values: z.infer<typeof schema>) => {
    if (!organizationUuid) return;

    if (project) {
      editProject(
        {
          organizationUuid,
          uuid: project.uuid,
          data: { title: values.title.trim() },
        },
        { onSuccess: () => onOpenChange(false) },
      );
    } else {
      createProject(
        { title: values.title.trim(), organizationUuid },
        {
          onSuccess: (data) => {
            onOpenChange(false);
            if (isUpdateCurrentProject && data) {
              onCurrentProjectChange(data.uuid);
            }
          },
        },
      );
    }
  };

  const title = useMemo(() => {
    if (project) {
      return t("projects.edit.title");
    }
    if (organizationName) {
      return t("projects.create.titleWithOrganization", { organizationName });
    }
    return t("projects.create.title");
  }, [organizationName, project, t]);

  return (
    <>
      <DialogHeader>
        <DialogTitle
          className="text-2xl text-center mb-3"
          data-testid="create-update-project-modal-title"
        >
          {title}
        </DialogTitle>
        <DialogDescription className="sr-only">{title}</DialogDescription>
      </DialogHeader>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
          <FormField
            control={form.control}
            name="title"
            render={({ field, fieldState: { error } }) => (
              <FormItem>
                <FormLabel>{t("projects.form.name")}</FormLabel>
                <FormControl>
                  <StyledInput
                    {...field}
                    isError={!!error}
                    placeholder={t("projects.form.placeholder")}
                    data-testid="create-update-project-modal-title-input"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="space-y-2 pt-4">
            <FormSubmitButton
              className="w-full h-12"
              isLoading={isCreating || isEditing}
              data-testid="create-update-project-modal-save-button"
            >
              {t("common:form_buttons.save")}
            </FormSubmitButton>
            <Button
              type="button"
              variant="outline"
              className="w-full h-12 border-none"
              onClick={() => onOpenChange(false)}
              data-testid="create-update-project-modal-cancel-button"
            >
              {t("common:form_buttons.cancel")}
            </Button>
          </div>
        </form>
      </Form>
    </>
  );
};

export interface ICreateUpdateProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationUuid: string | undefined;
  organizationName?: string;
  project?: IParsedProject | null;
  isUpdateCurrentProject?: boolean;
}

const CreateUpdateProjectDialog = ({
  open,
  organizationUuid,
  organizationName,
  onOpenChange,
  project,
  isUpdateCurrentProject = false,
}: ICreateUpdateProjectDialogProps) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <CreateUpdateProjectDialogContent
          onOpenChange={onOpenChange}
          organizationUuid={organizationUuid}
          organizationName={organizationName}
          project={project}
          isUpdateCurrentProject={isUpdateCurrentProject}
        />
      </DialogContent>
    </Dialog>
  );
};

export default CreateUpdateProjectDialog;
